import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";
import { MessageSquare, ThumbsUp } from "lucide-react";

import BaseButton from "@/components_v2/Button/BaseButton";
import {
  BUTTON_SIZES,
  BUTTON_TYPES,
  BUTTON_VARIANTS,
} from "@/components_v2/Button/constants";
import UserAvatar from "@/domains/users/components/UserAvatar";
import { needsEdit } from "@/domains/certifications/contributions";
import { ContributionItemProps } from "@/features/certifications/module/components/types";
import {
  discussionPath,
  editContributionPath,
} from "@/features/certifications/module/utils";
import {
  CONTRIBUTION_KINDS,
  CONTRIBUTION_STATUSES,
} from "@/domains/certifications/constants";

import "./styles.css";

const ICON_SIZE = 16;

const STATUS_TONES = {
  underReview: "warning",
  removed: "danger",
  countingPending: "info",
  counts: "info",
  doesNotCount: "warning",
} as const;

const ContributionItem = ({ item, user }: ContributionItemProps) => {
  const { t } = useTranslation();
  const { status, kind } = item;
  const isRemoved = status === CONTRIBUTION_STATUSES.REMOVED;

  return (
    <li className="module-activity flex gap-300">
      <UserAvatar user={user} />

      <div className="module-activity-body flex flex-col flex-1 gap-200">
        <div className="flex flex-wrap items-center gap-200">
          <span className="module-activity-chip body-xs-regular">
            {kind === CONTRIBUTION_KINDS.POST
              ? t("certification.module.community.yourPost")
              : t("certification.module.community.yourComment")}
          </span>

          {kind === CONTRIBUTION_KINDS.COMMENT && (
            <span className="certification-muted body-xs-regular">
              {t("certification.module.community.onDiscussion", {
                title: item.discussionTitle,
              })}
            </span>
          )}

          <span className="certification-muted body-xs-regular">
            {DateTime.fromISO(item.createdAt).toRelative()}
          </span>

          <span
            className={`module-status-tag module-status-tag-${STATUS_TONES[status]} body-xs-regular`}
          >
            {t(`certification.module.community.status.${status}`)}
          </span>
        </div>

        <p
          className={`module-activity-text body-s-regular ${
            isRemoved ? "module-activity-text-removed" : "module-body-text"
          }`}
        >
          {item.text}
        </p>

        {status === CONTRIBUTION_STATUSES.UNDER_REVIEW && (
          <p className="module-contribution-note module-contribution-note-neutral body-s-regular">
            {t("certification.module.community.notes.underReview")}
          </p>
        )}

        {needsEdit(status) && (
          <div
            className={`module-contribution-note module-contribution-note-${
              isRemoved ? "danger" : "warning"
            } flex flex-col gap-200`}
          >
            <p className="body-s-medium">
              {t(`certification.module.community.notes.${status}.title`, {
                context: isRemoved ? kind : undefined,
              })}
            </p>

            {item.reviewerFeedback && (
              <blockquote className="module-contribution-feedback flex flex-col gap-50">
                <span className="body-xs-regular">
                  {t("certification.module.community.notes.fromReviewer")}
                </span>

                <span className="body-s-regular">{item.reviewerFeedback}</span>
              </blockquote>
            )}

            <p className="body-s-regular">
              {t(`certification.module.community.notes.${status}.body`)}
            </p>

            <div>
              <BaseButton
                type={BUTTON_TYPES.LINK}
                variant={BUTTON_VARIANTS.LINK}
                size={BUTTON_SIZES.EXTRA_SMALL}
                to={editContributionPath(item)}
              >
                {t(`certification.module.community.edit.${kind}`)}
              </BaseButton>
            </div>
          </div>
        )}

        {!isRemoved && (
          <div className="certification-muted flex flex-wrap items-center gap-300 body-s-regular">
            <span className="flex items-center gap-100">
              <ThumbsUp size={ICON_SIZE} aria-hidden />

              {t("certification.module.community.likes", {
                count: item.likesCount,
              })}
            </span>

            {item.commentsCount !== null && (
              <span className="flex items-center gap-100">
                <MessageSquare size={ICON_SIZE} aria-hidden />

                {t("certification.module.community.comments", {
                  count: item.commentsCount,
                })}
              </span>
            )}

            <Link
              to={discussionPath(item.discussionId)}
              className="certification-link"
            >
              {kind === CONTRIBUTION_KINDS.POST
                ? t("certification.module.community.viewPost")
                : t("certification.module.community.viewComment")}
            </Link>
          </div>
        )}
      </div>
    </li>
  );
};

export default ContributionItem;
